import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Building, Users, Briefcase, Eye, CheckCircle, XCircle, Clock, Download } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

async function downloadApplicationPdf(app, creds, mode /* 'text' | 'full' */) {
  try {
    const withImages = mode === 'full';
    const res = await fetch(
      `${API}/trade-applications/${app.id}/pdf?include_images=${withImages}`,
      {
        method: 'GET',
        headers: { Authorization: 'Basic ' + btoa(`${creds.username}:${creds.password}`) },
      }
    );
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = (app.company_name || 'application').replace(/[^a-z0-9]/gi, '_');
    a.download = `trade_application_${safeName}${withImages ? '_full' : ''}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`PDF downloaded (${withImages ? 'full' : 'text only'})`);
  } catch (e) {
    toast.error(e?.message || 'PDF download failed');
  }
}

function PdfMenu({ app, creds, testIdPrefix }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`${testIdPrefix}-pdf-menu`}>
          <Download className="w-4 h-4 mr-1" /> PDF
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          data-testid={`${testIdPrefix}-pdf-text`}
          onClick={() => downloadApplicationPdf(app, creds, 'text')}
        >
          Text only (fast)
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={`${testIdPrefix}-pdf-full`}
          onClick={() => downloadApplicationPdf(app, creds, 'full')}
        >
          Full (with licence images)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function errMsg(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join('; ');
  if (typeof detail === 'string') return detail;
  if (e?.response?.status) return `${fallback} (${e.response.status})`;
  return e?.message || fallback;
}

function StatusBadge({ status }) {
  const styles = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700' };
  const icons = { pending: Clock, approved: CheckCircle, rejected: XCircle };
  const Icon = icons[status] || Clock;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
      <Icon className="w-3 h-3" /> {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
}

const formatCredit = (v) => {
  if (v === undefined || v === null || v === '') return '';
  const s = String(v).trim().replace(/^\$/, '');
  return `$${s}`;
};

function ApplicationDetail({ app, creds, onClose, onStatusUpdate }) {
  if (!app) return null;
  
  return (
    <Dialog open={!!app} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Application: {app.company_name}</span>
            <StatusBadge status={app.status} />
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-4"><Building className="w-5 h-5 text-[#01195d]" /> Company Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-slate-500">Company Name:</span><p className="font-medium">{app.company_name}</p></div>
              <div><span className="text-slate-500">Trading Name:</span><p className="font-medium">{app.trading_name || '-'}</p></div>
              <div><span className="text-slate-500">ABN:</span><p className="font-medium">{app.abn}</p></div>
              <div><span className="text-slate-500">Years Trading:</span><p className="font-medium">{app.years_trading || '-'}</p></div>
              <div className="col-span-2"><span className="text-slate-500">Trading Address:</span><p className="font-medium">{app.trading_address} {app.trading_postcode}</p></div>
              <div><span className="text-slate-500">Business Phone:</span><p className="font-medium">{app.business_phone || '-'}</p></div>
              <div><span className="text-slate-500">Fax:</span><p className="font-medium">{app.fax_number || '-'}</p></div>
              <div><span className="text-slate-500">Principal Contact:</span><p className="font-medium">{app.principal_contact}</p></div>
              <div><span className="text-slate-500">Email:</span><p className="font-medium">{app.email}</p></div>
              <div><span className="text-slate-500">Mobile:</span><p className="font-medium">{app.mobile}</p></div>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-4"><Users className="w-5 h-5 text-[#01195d]" /> Directors / Proprietors</h3>
            <div className="space-y-4">
              {app.directors?.map((d, i) => (
                <div key={i} className="bg-slate-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-slate-500">Name:</span><p className="font-medium">{d.name}</p></div>
                    <div><span className="text-slate-500">DOB:</span><p className="font-medium">{d.dob || '-'}</p></div>
                    <div className="col-span-2"><span className="text-slate-500">Address:</span><p className="font-medium">{d.address}</p></div>
                    {d.licence_file && (
                      <div className="col-span-2">
                        <span className="text-slate-500">Driver's Licence:</span>
                        <div className="mt-2">
                          {d.licence_file.startsWith('data:image') ? (
                            <img src={d.licence_file} alt="Licence" className="max-w-md rounded border" />
                          ) : (
                            <a href={d.licence_file} target="_blank" rel="noopener noreferrer" className="text-[#01195d] hover:underline flex items-center gap-1">
                              <FileText className="w-4 h-4" /> View Document
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-4"><Briefcase className="w-5 h-5 text-[#01195d]" /> Trade References</h3>
            <div className="space-y-3">
              {app.trade_references?.map((r, i) => (
                <div key={i} className="bg-slate-50 rounded-lg p-4 grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-slate-500">Company:</span><p className="font-medium">{r.company}</p></div>
                  <div><span className="text-slate-500">Contact:</span><p className="font-medium">{r.contact}</p></div>
                  <div><span className="text-slate-500">Phone:</span><p className="font-medium">{r.phone}</p></div>
                  <div><span className="text-slate-500">Email:</span><p className="font-medium">{r.email || '-'}</p></div>
                </div>
              ))}
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-slate-800 mb-4">Credit Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-slate-500">Monthly Credit:</span><p className="font-medium text-lg">{formatCredit(app.monthly_credit)}</p></div>
              <div><span className="text-slate-500">Signatory:</span><p className="font-medium">{app.signatory_name}</p></div>
              <div><span className="text-slate-500">Date:</span><p className="font-medium">{new Date(app.created_at).toLocaleDateString()}</p></div>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          <div className="flex gap-2 w-full justify-between">
            <div className="flex gap-2">
              <Button data-testid="close-application-modal" variant="outline" onClick={onClose}>Close</Button>
              <PdfMenu app={app} creds={creds} testIdPrefix="detail" />
            </div>
            <div className="flex gap-2">
              {app.status !== 'rejected' && (
                <Button data-testid="reject-application-button" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => onStatusUpdate(app.id, 'rejected')}>
                  <XCircle className="w-4 h-4 mr-1" /> Reject
                </Button>
              )}
              {app.status !== 'approved' && (
                <Button data-testid="approve-application-button" className="bg-green-600 hover:bg-green-700" onClick={() => onStatusUpdate(app.id, 'approved')}>
                  <CheckCircle className="w-4 h-4 mr-1" /> Approve
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TradeApplicationsTab({ creds }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);

  const auth = { auth: creds };

  useEffect(() => { loadApplications(); }, []);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/trade-applications`, auth);
      setApplications(r.data);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load applications'));
    } finally {
      setLoading(false);
    }
  };

  const viewApplication = async (appId) => {
    try {
      const r = await axios.get(`${API}/trade-applications/${appId}`, auth);
      setSelectedApp(r.data);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load details'));
    }
  };

  const updateStatus = async (appId, status) => {
    try {
      await axios.put(`${API}/trade-applications/${appId}/status?status=${status}`, {}, auth);
      toast.success(`Application ${status}`);
      loadApplications();
      if (selectedApp?.id === appId) setSelectedApp(null);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to update status'));
    }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading applications...</div>;

  return (
    <div data-testid="trade-applications-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-slate-800">Trade Applications</h2>
        <span className="text-sm text-slate-500" data-testid="applications-count">{applications.length} applications</span>
      </div>

      {applications.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center" data-testid="no-applications">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Applications Yet</h3>
          <p className="text-slate-500">Trade credit applications will appear here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full" data-testid="applications-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Company</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Contact</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Credit</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Date</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Status</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {applications.map(app => (
                <tr key={app.id} className="hover:bg-slate-50" data-testid={`application-row-${app.id}`}>
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-800">{app.company_name}</div>
                    <div className="text-sm text-slate-500">ABN: {app.abn}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-slate-800">{app.principal_contact}</div>
                    <div className="text-sm text-slate-500">{app.email}</div>
                  </td>
                  <td className="px-6 py-4"><span className="font-medium text-slate-800">{formatCredit(app.monthly_credit)}/mo</span></td>
                  <td className="px-6 py-4 text-sm text-slate-600">{new Date(app.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4"><StatusBadge status={app.status} /></td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex gap-2">
                      <PdfMenu app={app} creds={creds} testIdPrefix={`row-${app.id}`} />
                      <Button size="sm" variant="outline" onClick={() => viewApplication(app.id)} data-testid={`view-application-${app.id}`}>
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ApplicationDetail app={selectedApp} creds={creds} onClose={() => setSelectedApp(null)} onStatusUpdate={updateStatus} />
    </div>
  );
}

export default TradeApplicationsTab;
